'use client';

import { Icon } from '@polyseme/design-system';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import React from 'react';

import { Badge } from '../components/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/tabs';
import { Title } from '../components/title';
import { Typography } from '../components/typography';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const compareItems = [
  {
    key: 'connectPartner',
    icon: 'server-cog',
  },
  {
    key: 'addProduct',
    icon: 'circle-fading-plus',
  },
  {
    key: 'customizeProduct',
    icon: 'wand-sparkles',
  },
  {
    key: 'addDistributor',
    icon: 'user-cog',
  },
] as const;

export const Compare = () => {
  const t = useTranslations('landing.polyseme.compare');
  return (
    <motion.section
      className="mx-auto mb-[120px] mt-[60px] w-full max-w-screen-xl font-sans"
      initial="hidden"
      transition={{ duration: 0.5, ease: 'easeOut' }}
      variants={fadeInUp}
      viewport={{ once: true, amount: 0.2 }}
      whileInView="visible"
    >
      {/* Mobile: Tabs */}
      <div className="mb-8 sm:hidden">
        <Tabs className="flex flex-col" defaultValue="with">
          <TabsList>
            <TabsTrigger className="gap-2" value="with">
              {t.rich('withPolyseme', {
                highlight: (children) => (
                  <span className="text-primary">{children}</span>
                ),
              })}
            </TabsTrigger>
            <TabsTrigger value="without">{t('withoutPolyseme')}</TabsTrigger>
          </TabsList>

          <TabsContent value="with">
            {compareItems.map((item, i) => (
              <article key={i} className="border-b py-6">
                <div className="flex flex-col items-center gap-3 text-center text-gray-700">
                  <span className="grid size-12 place-items-center rounded-full border">
                    <Icon className="size-5" name={item.icon} />
                  </span>
                  <Typography className="md:whitespace-pre-wrap">
                    {t(`items.${item.key}.action`)}
                  </Typography>
                  <Badge size="lg" variant="info">
                    {t(`items.${item.key}.withPoly`)}
                  </Badge>
                </div>
              </article>
            ))}
          </TabsContent>

          <TabsContent value="without">
            {compareItems.map((item, i) => (
              <article key={i} className="border-b py-6">
                <div className="flex flex-col items-center gap-3 text-center text-gray-700">
                  <span className="grid size-12 place-items-center rounded-full border">
                    <Icon className="size-5" name={item.icon} />
                  </span>
                  <Typography className="md:whitespace-pre-wrap">
                    {t(`items.${item.key}.action`)}
                  </Typography>
                  <Badge size="lg" variant="neutral">
                    {t(`items.${item.key}.withoutPoly`)}
                  </Badge>
                </div>
              </article>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Desktop: side-by-side comparison grid */}
      <div className="hidden sm:block">
        <header className="mb-10 grid w-full grid-cols-3 items-center">
          <Title as="h3" className="text-center" variant="title-3">
            {t.rich('withPolyseme', {
              highlight: (children) => (
                <span className="text-primary">{children}</span>
              ),
            })}
          </Title>
          <span className="text-center text-gray-500">{t('vs')}</span>
          <Title as="h3" className="text-center" variant="title-3">
            {t('withoutPolyseme')}
          </Title>
        </header>

        {compareItems.map((item, index) => (
          <article
            key={index}
            className="grid w-full grid-cols-3 border-b py-4"
          >
            <div className="flex items-center justify-center">
              <Badge size="lg" variant="info">
                {t(`items.${item.key}.withPoly`)}
              </Badge>
            </div>

            <div className="flex flex-1 flex-col items-center gap-3 px-4 text-center text-gray-700">
              <span className="grid size-12 place-items-center rounded-full border">
                <Icon className="size-5" name={item.icon} />
              </span>
              <Typography className="md:whitespace-pre-wrap">
                {t(`items.${item.key}.action`)}
              </Typography>
            </div>

            <div className="flex items-center justify-center">
              <Badge size="lg" variant="neutral">
                {t(`items.${item.key}.withoutPoly`)}
              </Badge>
            </div>
          </article>
        ))}
      </div>
    </motion.section>
  );
};
